import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPublishGate, persistHealthRun, PublishGate } from 'services/health';

export type UsePublishGateResult = {
  gate: PublishGate | null;
  loading: boolean;
  persisting: boolean;
  refresh: () => Promise<void>;
  persistRun: () => Promise<void>;
};

// Shared by the Health screen (verify & persist) and the Publish screen (consume the verdict).
const usePublishGate = (courseId: number): UsePublishGateResult => {
  const [gate, setGate] = useState<PublishGate | null>(null);
  const [loading, setLoading] = useState(true);
  const [persisting, setPersisting] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    const result = await fetchPublishGate(courseId);
    if (!mountedRef.current) {
      return;
    }
    setGate(result && result.status ? result : null);
    setLoading(false);
  }, [courseId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const persistRun = useCallback(async () => {
    setPersisting(true);
    const result = await persistHealthRun(courseId);
    if (!mountedRef.current) {
      return;
    }
    if (result?.gate) {
      setGate(result.gate);
    } else {
      await refresh();
    }
    setPersisting(false);
  }, [courseId, refresh]);

  return { gate, loading, persisting, refresh, persistRun };
};

export default usePublishGate;
